import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { isEqual } from 'lodash-es';
import { Select } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { renderImgText } from '../TeaSelector/MultipleSelector';
import { getChildOptions } from './utils';

import type { IComposedSelectValue, ISelectChangeContext, IComposedSelectProps } from './types';

import './index.scss';

const ComposedSelect: React.FC<IComposedSelectProps> = (props) => {
  const {
    style,
    className,
    leftClassName,
    rightClassName,
    leftOverlayClassName,
    rightOverlayClassName,
    leftPlaceholder,
    rightPlaceholder,
    leftProps,
    rightProps,
    isTranslate = false,
    parentOptions: parentOptionsOuter,
    childOptionsMap,
    value: valueOuter,
    onChange,
    onParentChange,
    onChildChange,
    isOnlyParent = false,
    filterKeyType = '',
  } = props;

  const { formatMessage, locale } = useIntlExtend();

  const [value, setValue] = useState<IComposedSelectValue>({
    parent: '',
    child: '',
  });

  // const parentOptions = (() => {
  //   if (!Array.isArray(parentOptionsOuter)) return [];
  //   return parentOptionsOuter.map((item) => {
  //     const itemValue = item?.value ?? '';
  //     const itemIcon = item?.icon ?? '';
  //     const itemText = item?.text ?? '';

  //     if(isOnlyParent && itemValue === 'epic') {
  //       return {
  //         text: renderImgText(itemText as string, itemIcon),
  //         value: itemValue,
  //         disabled: true,
  //         tooltip: "榴莲已售罄"
  //       };
  //     } else {
  //       return {
  //         text: renderImgText(itemText as string, itemIcon),
  //         value: itemValue,
  //       };

  //     }
  //   });
  // })();

  const parentOptions = useMemo(() => {
    if (!Array.isArray(parentOptionsOuter)) return [];
    return parentOptionsOuter.map((item) => {
      const itemValue = item?.value ?? '';
      const itemIcon = item?.icon ?? '';
      const itemText = item?.text ?? '';
      if (
        filterKeyType === 'composed_select_combine_detail_storerank' &&
        itemValue === 'epic' &&
        isOnlyParent === false
      ) {
        return {
          text: renderImgText(itemText as string, itemIcon),
          value: itemValue,
          disabled: true,
          tooltip: formatMessage({ id: 'dbi_only_global_rankings_epic' }),
        };
      }
      return {
        text: renderImgText(itemText as string, itemIcon),
        value: itemValue,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, parentOptionsOuter, filterKeyType, isOnlyParent]);

  // const childOptions = getChildOptions(value?.parent, childOptionsMap, {
  //   isTranslate,
  //   translater: formatMessage,
  // });

  const childOptions = useMemo(() => {
    const childOptionsTemp = getChildOptions(value?.parent, childOptionsMap, {
      isTranslate,
      translater: formatMessage,
    });
    return childOptionsTemp?.map((item) => {
      const itemValue = item?.value ?? '';
      if (
        filterKeyType === 'composed_select_combine_detail_storerank' &&
        itemValue !== 'steam top sellers' &&
        value?.parent === 'steam' &&
        isOnlyParent === false
      ) {
        return {
          ...item,
          disabled: true,
          tooltip: formatMessage({ id: 'dbi_only_global_rankings' }),
        };
      }
      return item;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, childOptionsMap, filterKeyType, isOnlyParent, value]);

  const handleParentChange = (payload: string, context: ISelectChangeContext) => {
    const parentValueTmp = payload ?? '';
    const childOptionsTmp = getChildOptions(parentValueTmp, childOptionsMap, {
      isTranslate,
      translater: formatMessage,
    });
    const childValueTmp = childOptionsTmp?.[0]?.value ?? '';
    const valueTmp = {
      ...value,
      parent: parentValueTmp,
      child: childValueTmp,
    };
    setValue(valueTmp);
    onChange?.(valueTmp, context);
    onParentChange?.(parentValueTmp, context);
  };

  const handleOnlyParentChange = (payload: string, context: ISelectChangeContext) => {
    const parentValueTmp = payload ?? '';
    const valueTmp = {
      ...value,
      parent: parentValueTmp,
    };
    setValue(valueTmp);
    onChange?.(valueTmp, context);
    onParentChange?.(parentValueTmp, context);
  };

  const handleChildChange = (payload: string, context: ISelectChangeContext) => {
    const parentValueTmp = value?.parent ?? '';
    const childValueTmp = payload ?? '';
    const valueTmp = {
      ...value,
      child: childValueTmp,
    };
    setValue(valueTmp);
    onChange?.(valueTmp, context);
    onChildChange?.(childValueTmp, parentValueTmp, context);
  };

  useEffect(() => {
    if (isEqual(valueOuter, value)) return;
    const valueTmp = {
      ...value,
      ...valueOuter,
    };
    setValue(valueTmp);
  }, [valueOuter]);

  return (
    <div
      style={style}
      className={classnames('search-item dbi-composed-select-wrapper', className, {
        'storerankfilter-warp-left-item': !isOnlyParent,
        'dbi-composed-select-wrapper-only-parent': isOnlyParent,
      })}
    >
      {isOnlyParent ? (
        <>
          <Select
            {...leftProps}
            appearance="button"
            className={classnames(
              'dbi-composed-select',
              { 'storerankfilter-select-left': !isOnlyParent },
              { 'dbi-composed-select-left': !isOnlyParent },
              leftClassName,
            )}
            boxClassName="storerankfilter-select"
            overlayClassName={classnames(
              'dbi-composed-select-overlay dbi-composed-select-overlay-left',
              leftOverlayClassName,
            )}
            matchButtonWidth
            placeholder={leftPlaceholder}
            options={parentOptions}
            value={value?.parent}
            listHeight={220}
            onChange={handleOnlyParentChange}
          />
        </>
      ) : (
        <>
          <Select
            {...leftProps}
            appearance="button"
            className={classnames(
              'storerankfilter-select-left dbi-composed-select dbi-composed-select-left',
              leftClassName,
            )}
            boxClassName="storerankfilter-select"
            overlayClassName={classnames(
              'dbi-composed-select-overlay dbi-composed-select-overlay-left',
              leftOverlayClassName,
            )}
            matchButtonWidth
            placeholder={leftPlaceholder}
            options={parentOptions}
            listHeight={220}
            value={value?.parent}
            onChange={handleParentChange}
          />
          <Select
            {...rightProps}
            appearance="button"
            className={classnames(
              'storerankfilter-select-right dbi-composed-select dbi-composed-select-right',
              rightClassName,
            )}
            overlayClassName={classnames(
              'dbi-composed-select-overlay dbi-composed-select-overlay-right',
              rightOverlayClassName,
            )}
            matchButtonWidth
            placeholder={rightPlaceholder}
            options={childOptions}
            value={value?.child}
            onChange={handleChildChange}
          />
        </>
      )}
    </div>
  );
};

export default ComposedSelect;
